import { Suspense, useState } from 'react';
import { Spinner } from '@dpem/design-system';
import { Outlet, useLocation } from 'react-router-dom';

import { AppErrorBoundary } from '@/app/errors/AppErrorBoundary';
import { ShellHeader } from './ShellHeader';
import { ShellRail } from './ShellRail';
import { ShellSubhead } from './ShellSubhead';
import { SurfaceChromeProvider } from './surface-chrome';
import styles from './AppShell.module.css';

/**
 * The chrome every authenticated surface renders inside.
 *
 * Composition, not layout logic: header band, icon rail, subhead, and one `main`
 * that the router fills. The two wrappers around the outlet are the interesting
 * part.
 *
 * `AppErrorBoundary` is keyed on the pathname, so a surface that throws is
 * contained to the content area — the navigation survives, and moving away from
 * the broken route clears the error without a reload.
 *
 * `Suspense` is here rather than around the whole app because the surfaces are
 * lazily imported. A fallback at the root would blank the header and rail on every
 * first visit to a surface, which reads as a page load in an app that is meant to
 * feel like one workspace.
 */

export function AppShell() {
  const { pathname } = useLocation();
  const [railExpanded, setRailExpanded] = useState(true);

  return (
    <SurfaceChromeProvider>
      <div className={styles.shell}>
        <a className={styles.skipLink} href="#surface">
          Skip to content
        </a>

        <ShellHeader
          railExpanded={railExpanded}
          onToggleRail={() => setRailExpanded((value) => !value)}
        />

        <div className={styles.body}>
          {railExpanded && <ShellRail />}

          <main className={styles.main}>
            <ShellSubhead />
            {/* `id` is the skip link's target and `tabIndex={-1}` is what makes it
                focusable, so activating the link moves the caret rather than only
                scrolling. */}
            <div className={styles.surface} id="surface" tabIndex={-1}>
              <AppErrorBoundary resetKey={pathname}>
                <Suspense fallback={<SurfacePending />}>
                  <Outlet />
                </Suspense>
              </AppErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </SurfaceChromeProvider>
  );
}

function SurfacePending() {
  return (
    <div className={styles.surfacePending}>
      <Spinner size="md" label="Loading workspace" />
    </div>
  );
}
